// A deck's PowerPoint templates as cached server state (AB#179, per template since AB#445).
//
// A deck is drawn on one or several templates of its bid's library (slides/deck-templates.ts): its
// DIRECTORY (`GET /v1/slides/templates?room=`: the deck default pair and the library) is read
// first, then the VIEW of each (template, master) pair the deck's slides use — and only those, plus
// the deck default pair "New slide" lands on (`GET /v1/bids/:bid/office-templates/:id/slides-view
// ?master=`). A layout menu lists names from the directory alone and, while open, loads the views of
// the groups it shows, a few at a time (AB#480).
//
// The editor draws NO slide until the directory and the used views have settled: a deck drawn with
// the default theme and then redrawn ~1 s later with its template's layout is a visible flash. The
// queries are cached per bid — the directory per room, each view per (template, version, master) —
// so reopening a slides part, or switching between parts of the same bid, draws from the cache at
// once; Canvas prefetches the directory and the deck default's view as soon as a slides part is
// selected, in parallel with the editor's chunk and the room's sync (AB#283). The editor then waits
// (briefly, bounded) for the pictures of the masters and of the layouts the deck uses (background,
// logo), so the first slide shown already has its background; a picture later than that pops in.
// Once a deck has been drawn it never goes back to its loading state: a pair its slides start using
// later (a teammate moved one) draws when its view arrives.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useIsFetching, useQueries, useQuery, useQueryClient, type Query, type QueryClient, type QueryKey } from '@tanstack/react-query'
import { api, type DeckTemplateRef, type DeckTemplates } from '../lib/api'
import { apiUrl } from '../lib/backend'
import type { LayoutChoice } from './deck'
import { findLayoutChoice } from './deck'
import {
  choiceTemplate,
  deckRefOf,
  layoutGroups,
  menuTemplateRefs,
  refKey,
  refOfSlide,
  templateOf,
  themeOfView,
  usedTemplateRefs,
  type LayoutGroup,
  type SlideTemplateKeys,
} from './deck-templates'
import type { DrawingFill, DrawingNode, RenderTheme, SlidesTemplateLike } from './theme'

/**
 * Every slides-template query of a bid sits under this prefix: one invalidation reaches its decks'
 * directories and every view.
 */
export const slidesTemplateKey = (bid: string) => ['slides-template', bid] as const
/** A deck's directory (its room key; '' = an editor mounted without one). */
export const deckTemplatesKey = (bid: string, room: string) => ['slides-template', bid, 'deck', room] as const
/** One template's view on one master, at one version: a new version is a new key, so it redraws. */
export const templateViewKey = (bid: string, templateId: string, version: number, master: number) =>
  ['slides-template', bid, templateId, version, master] as const
/** A template changes when an admin uploads another one: rare, and a write invalidates it anyway. */
export const SLIDES_TEMPLATE_STALE_MS = 5 * 60_000
/** The longest the first frame waits for the masters' pictures: a slow blob never blocks the deck. */
export const PRELOAD_TIMEOUT_MS = 1200
/** A layout menu asks for a view only while fewer than this many are in flight (AB#480). */
export const MENU_VIEW_REQUESTS = 4

/**
 * Every picture the template's master and layouts draw (backgrounds, logos, pictures), deduplicated
 * — of every layout, or only of the layouts `used` (by id) when given.
 */
export function masterImages(tpl: SlidesTemplateLike | null, used?: ReadonlySet<string>): string[] {
  if (!tpl) return []
  const out = new Set<string>()
  const fill = (f: DrawingFill | null | undefined): void => {
    if (f?.type === 'picture' && f.image) out.add(f.image)
  }
  const node = (n: DrawingNode): void => {
    fill(n.fill)
    // The renderer draws a picture's SVG when it has one, else its raster (render.ts).
    const src = n.svg ?? n.image
    if (n.kind === 'picture' && src) out.add(src)
    for (const c of (n.children ?? []) as DrawingNode[]) node(c)
  }
  fill(tpl.background)
  tpl.shapes?.forEach(node)
  for (const l of tpl.layouts) {
    if (used && !used.has(l.id)) continue
    fill(l.background)
    l.shapes?.forEach(node)
  }
  return [...out]
}

/** Fetch the pictures into the browser's cache; resolves when all are in (or failed), or at `timeoutMs`. */
export function preloadImages(urls: string[], timeoutMs = PRELOAD_TIMEOUT_MS): Promise<void> {
  if (!urls.length || typeof Image === 'undefined') return Promise.resolve()
  let timer: ReturnType<typeof setTimeout> | undefined
  const all = Promise.all(
    urls.map(
      (src) =>
        new Promise<void>((done) => {
          const img = new Image()
          img.onload = () => done()
          img.onerror = () => done()
          img.src = src
        }),
    ),
  ).then(() => undefined)
  const late = new Promise<void>((done) => {
    timer = setTimeout(done, timeoutMs)
  })
  return Promise.race([all, late]).finally(() => clearTimeout(timer))
}

export const deckQuery = (bid: string, room: string) => ({
  queryKey: deckTemplatesKey(bid, room),
  queryFn: (): Promise<DeckTemplates> => api.deckTemplates(room),
  staleTime: SLIDES_TEMPLATE_STALE_MS,
  // A failure falls back to the default theme: retrying would only hold the deck back longer.
  retry: false,
})

const viewQuery = (bid: string, templateId: string, version: number, master: number) => ({
  queryKey: templateViewKey(bid, templateId, version, master),
  queryFn: async (): Promise<SlidesTemplateLike | null> => (await api.officeTemplateSlidesView(bid, templateId, master)).template,
  staleTime: SLIDES_TEMPLATE_STALE_MS,
  retry: false,
})

/**
 * Refetch the bid's slides templates — after any write to its PowerPoint templates (the library
 * lists, the artefact template picker, which changes a deck default), so an open slides editor
 * redraws without a reload: every deck directory and every view of the bid.
 */
export function invalidateSlidesTemplates(client: QueryClient, bid: string): Promise<void> {
  return client.invalidateQueries({ queryKey: slidesTemplateKey(bid) })
}

/**
 * Start fetching a deck's templates before the editor mounts (Canvas, on selecting a slides part):
 * its directory, then its default pair's view (the one every slide without a template of its own is
 * drawn on). A failure is left to the editor, which falls back to the default theme.
 */
export async function prefetchDeckTemplates(client: QueryClient, bid: string, room: string): Promise<void> {
  await client.prefetchQuery(deckQuery(bid, room))
  const dir = client.getQueryData<DeckTemplates>(deckTemplatesKey(bid, room)) ?? null
  const deck = deckRefOf(dir)
  const t = deck && templateOf(dir, deck.templateId)
  if (deck && t) await client.prefetchQuery(viewQuery(bid, t.id, t.version, deck.master))
}

/** Views whose pictures were already waited for in this tab: a reopen does not wait again. */
const warmed = new WeakSet<SlidesTemplateLike>()
/** Views whose pictures are being fetched: two decks (or two runs) share one wait. */
const warming = new WeakMap<SlidesTemplateLike, Promise<void>>()
function warm(view: SlidesTemplateLike, used: ReadonlySet<string>): Promise<void> {
  let p = warming.get(view)
  if (!p) {
    p = preloadImages(masterImages(view, used).map((u) => apiUrl(u))).then(() => {
      warmed.add(view)
    })
    warming.set(view, p)
  }
  return p
}
const pictureless = new WeakMap<SlidesTemplateLike, boolean>()
/** A view that draws no picture at all has nothing to wait for. */
function drawsNoPicture(view: SlidesTemplateLike): boolean {
  let none = pictureless.get(view)
  if (none === undefined) {
    none = masterImages(view).length === 0
    pictureless.set(view, none)
  }
  return none
}

/** The previous array while every item is the same (a stable dependency for memos). */
function useSameItems<T>(next: readonly T[]): readonly T[] {
  const prev = useRef(next)
  if (prev.current.length !== next.length || prev.current.some((v, i) => v !== next[i])) prev.current = next
  return prev.current
}

/** Whether a query is a view of this bid: its directories' keys are one shorter than templateViewKey's. */
const isViewOf = (bid: string) => (q: Query): boolean => q.queryKey.length === 5 && q.queryKey[0] === 'slides-template' && q.queryKey[1] === bid

const NO_PAIRS: ReadonlySet<string> = new Set()

/**
 * Which pairs of an open menu may fetch (by refKey), given the ones allowed so far. What costs no
 * request is allowed at once (fresh in the cache, or already in flight); the others follow in the
 * menu's order, while fewer than MENU_VIEW_REQUESTS views are in flight: the slides' own count, though
 * they never wait. A pair out of `refs` (collapsed, filtered out, the menu closed) drops out: never
 * requested if it was waiting; a request already in flight still lands in the cache.
 */
function allowedMenuPairs(
  qc: QueryClient,
  bid: string,
  refs: readonly DeckTemplateRef[],
  keyOf: (ref: DeckTemplateRef) => QueryKey,
  was: ReadonlySet<string>,
): ReadonlySet<string> {
  let room = MENU_VIEW_REQUESTS - qc.isFetching({ predicate: isViewOf(bid) })
  const next = new Set<string>()
  for (const ref of refs) {
    const k = refKey(ref)
    const state = qc.getQueryState(keyOf(ref))
    const fresh = !!state && state.data !== undefined && !state.isInvalidated && Date.now() - state.dataUpdatedAt < SLIDES_TEMPLATE_STALE_MS
    if (was.has(k) || fresh || state?.fetchStatus === 'fetching') next.add(k)
    else if (room > 0) {
      room--
      next.add(k)
    }
  }
  return next.size === was.size && [...next].every((k) => was.has(k)) ? was : next
}

/** A master its author left unnamed, in a menu heading (1-based); the editor passes its own words. */
const unnamedMaster = (n: number): string => `Master ${n}`

/** What the editor reads of a deck's templates. */
export interface DeckTemplatesState {
  /**
   * False while the directory, or a view the deck uses, is first fetched (no cached data), then
   * while the pictures of those views (their masters, the layouts the deck uses) are fetched — at
   * most PRELOAD_TIMEOUT_MS, once per view. An error, a deck without a template (`deck: null`) or
   * no bid is ready with the default theme and the built-in layouts. Once true for a deck, it stays.
   */
  ready: boolean
  /** The deck's directory (null: none — no bid, a failure, or still loading). */
  directory: DeckTemplates | null
  /** The pair a slide is drawn on (null = no template). */
  refOf: (slide: SlideTemplateKeys) => DeckTemplateRef | null
  /**
   * A slide's render theme: its own template on the master of its layout, the default theme while
   * that view is not in (or without a template). ONE object per view: the render contexts built
   * on it stay the same, and so do the memoised thumbnails (AB#283).
   */
  themeOf: (slide: SlideTemplateKeys) => RenderTheme
  /**
   * The layout menus' groups (template › master › layout), the deck default's pair first: every pair
   * of the directory, a pair whose view is not in listing the directory's names (`unavailable`).
   */
  groups: LayoutGroup[]
  /** The layout choice a slide is on, found in its own template and master (null = unknown). */
  choiceOf: (slide: SlideTemplateKeys) => LayoutChoice | null
  /** A menu is open and a view it shows is still to come. */
  loadingMore: boolean
}

/**
 * A deck's templates: its directory, the views of the pairs its `slides` use (and of its default
 * pair), and the views of the groups an open layout menu shows (`menu`: their keys, null = no menu
 * open), which wait for room (allowedMenuPairs). `room` is the deck's room key; without one (a bare
 * render), only what the cache holds for '' is read. `masterN` names a master its author left
 * unnamed, in the menus' headings.
 */
export function useDeckTemplates(
  bid: string | null,
  room: string | undefined,
  slides: readonly SlideTemplateKeys[],
  { menu = null, masterN = unnamedMaster }: { menu?: readonly string[] | null; masterN?: (n: number) => string } = {},
): DeckTemplatesState {
  const qc = useQueryClient()
  const roomKey = room ?? ''
  const dirQ = useQuery({ ...deckQuery(bid ?? '', roomKey), enabled: !!bid && !!room })
  const directory = dirQ.data ?? null
  // Nothing to ask (no bid, no room) is settled: whatever the cache holds, else no template.
  const dirSettled = !bid || !room || dirQ.data !== undefined || dirQ.isError

  // The pairs the slides use, as a key that changes only when one does (not on every keystroke).
  const usedKey = usedTemplateRefs(directory, slides).map(refKey).join('\n')
  const slidesNow = useRef(slides)
  slidesNow.current = slides
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const used = useMemo(() => usedTemplateRefs(directory, slidesNow.current), [directory, usedKey])
  const usedKeys = useMemo(() => new Set(used.map(refKey)), [used])
  // What only a menu asks for, in the menu's order; its key changes only when a shown group does.
  const menuKey = menu?.join('\n') ?? ''
  const menuRefs = useMemo(() => {
    const shown = new Set(menu ?? [])
    return menuTemplateRefs(directory).filter((r) => shown.has(refKey(r)) && !usedKeys.has(refKey(r)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directory, usedKeys, menuKey])
  const wanted = useMemo(() => [...used, ...menuRefs], [used, menuRefs])
  const [allowed, setAllowed] = useState(NO_PAIRS)
  const viewKeyOf = (ref: DeckTemplateRef): QueryKey =>
    templateViewKey(bid ?? '', ref.templateId, templateOf(directory, ref.templateId)?.version ?? 0, ref.master)

  const results = useQueries({
    queries: wanted.map((ref) => {
      const t = templateOf(directory, ref.templateId)
      const version = t?.version ?? 0
      return {
        ...viewQuery(bid ?? '', ref.templateId, version, ref.master),
        enabled: !!bid && (usedKeys.has(refKey(ref)) || allowed.has(refKey(ref))),
        // A new version of a template already drawn: keep drawing the last one until it is in, so a
        // library write never flashes the deck (any cached version of the same pair will do).
        placeholderData: (): SlidesTemplateLike | null | undefined => {
          for (const [key, data] of qc.getQueriesData<SlidesTemplateLike | null>({ queryKey: ['slides-template', bid ?? '', ref.templateId] })) {
            if (key[4] === ref.master && key[3] !== version && data !== undefined) return data
          }
          return undefined
        },
      }
    }),
  })
  // After useQueries' own effect, which starts what was just allowed: the count below includes it.
  const inFlight = useIsFetching({ predicate: isViewOf(bid ?? '') })
  useEffect(() => {
    setAllowed(allowedMenuPairs(qc, bid ?? '', menuRefs, viewKeyOf, allowed))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuRefs, inFlight, allowed])
  // A pair's view: the data (null = it has none), null on an error, undefined while it loads.
  const views = useSameItems(wanted.map((_, i) => {
    const r = results[i]
    return r?.data !== undefined ? r.data : r?.isError ? null : undefined
  }))
  const viewByKey = useMemo(() => new Map(wanted.map((ref, i) => [refKey(ref), views[i]] as const)), [wanted, views])

  // The layouts the deck uses, per pair: the only layout pictures the first frame waits for.
  const usedLayouts = useMemo(() => {
    const out = new Map<string, Set<string>>()
    for (const s of slidesNow.current) {
      const k = refKey(refOfSlide(directory, s))
      if (!s.layout) continue
      const set = out.get(k) ?? new Set<string>()
      set.add(s.layout)
      out.set(k, set)
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directory, usedKey])
  const usedViews = used.map((ref) => viewByKey.get(refKey(ref)))
  // The used views whose pictures the first frame still waits for (once per view, per tab).
  const [, setWarmed] = useState(0)
  const toWarm = useSameItems(usedViews.filter((v): v is SlidesTemplateLike => !!v && !warmed.has(v) && !drawsNoPicture(v)))
  const usedLayoutsNow = useRef(usedLayouts)
  usedLayoutsNow.current = usedLayouts
  useEffect(() => {
    if (!toWarm.length) return
    let live = true
    const keyOf = new Map(used.map((ref) => [viewByKey.get(refKey(ref)), refKey(ref)] as const))
    void Promise.all(toWarm.map((v) => warm(v, usedLayoutsNow.current.get(keyOf.get(v) ?? '') ?? new Set()))).then(() => {
      if (live) setWarmed((n) => n + 1)
    })
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toWarm])

  const settled =
    dirSettled && usedViews.every((v) => v !== undefined) && usedViews.every((v) => !v || drawsNoPicture(v) || warmed.has(v))
  // Once drawn, a deck stays drawn (see the header).
  const scope = `${bid ?? ''}\u0000${roomKey}`
  const drawn = useRef<string | null>(null)
  if (settled) drawn.current = scope

  const groups = useMemo(() => layoutGroups(directory, (ref) => viewByKey.get(refKey(ref)), masterN), [directory, viewByKey, masterN])
  const refOf = useMemo(() => (slide: SlideTemplateKeys) => refOfSlide(directory, slide), [directory])
  const themeOf = useMemo(
    () => (slide: SlideTemplateKeys) => {
      const ref = refOfSlide(directory, slide)
      return ref ? themeOfView(viewByKey.get(refKey(ref))) : themeOfView(null)
    },
    [directory, viewByKey],
  )
  const choiceOf = useMemo(() => {
    // Only what a view drew: a layout listed from the directory has no placeholders to write.
    const every = groups.flatMap((g) => (g.unavailable ? [] : g.layouts))
    return (slide: SlideTemplateKeys): LayoutChoice | null => {
      const ref = refOfSlide(directory, slide)
      return findLayoutChoice(every, slide.layout, { template: choiceTemplate(ref, directory), ...(ref ? { master: ref.master } : {}) })
    }
  }, [groups, directory])

  return {
    ready: !bid || settled || drawn.current === scope,
    directory,
    refOf,
    themeOf,
    groups,
    choiceOf,
    loadingMore: menu !== null && views.some((v) => v === undefined),
  }
}
